define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"dojo/dom-construct",
	"dojo/query",
	"underscore",
	"csi/def",
	"csi/def/ActionFactory",
	"csi/def/Heading",
	"csi/def/View",
	"csi/heading/_base",
	"csi/html",
	"csi/reflection",
	"csi/util",
	"./Action",
	"./HeadingLabel",
	"./Label",
	"./Rule",
	"./ViewRef"
], function(declare, lang, domConstruct, query, _, csiDef, ActionFactory, Heading, View, headingBase, html, reflection, util,
	Action, HeadingLabel, Label, Rule, ViewRef) {

	/**
	 * A function returning true or false indicating if the User Default is applicable
	 * @callback htmlTemplateProcessKeyCallback
	 * @param {Context} ctx
	 * @param {string} key
	 * @param {Object} def
	 * @param {DOMNode} domNode
	 */

	var TAGS = {
		"Card": Heading,
		"Heading": Heading,
		"HeadingLabel": HeadingLabel,
		"Image": Heading,
		"Label": Label,
		"Rule": Rule,
		"ViewRef": ViewRef
	};

	function _defaultProcessKey(ctx, key, def, domNode) {
		if (def) {
			headingBase.toHtml(ctx, def, domNode);
		}
	}

	function _processRules(/*Context*/ctx, /*DOMNode*/domRoot, /*{String:Heading|Rule}*/defs) {
		// summary: apply rule definitions to HTML
		var attrName = html.DATA_TEMPLATE_RULE;
		var b;

		query(html.getDataAttributeSelector(attrName), domRoot).forEach(function(domNode) {
			var rule = defs[html.getDataAttribute(domNode, attrName)];
			if (rule) {
				html.removeDataAttribute(domNode, attrName);

				// process rule if not marked as for print, or if printing
				if (!rule.bPrint || ctx.bPrint) {
					rule.keyToHtml(ctx, domNode);
					b = true;
				}
			}
		});

		// remove elements that are marked with "hideFromView" class - do this so that child elements with keys won't even have to be processed
		if (b) {
			query("." + html.CSS_HIDE, domRoot).orphan();
		}
	}

	// FIX: where to put this - doesn't really belong in html
	lang.mixin(html, {
		DATA_TEMPLATE_KEY_REPEAT: "*",

		/**
		 * Add content from supplied defs to keyed elements in template
		 * @param {Context} ctx
		 * @param {string|DOMNode} template
		 * @param {Object<string,Object>} defs - object mapping keys to Heading or Rule
		 * @param {htmlTemplateProcessKeyCallback} [fProcessKey] - custom function to process the key
		 * @param {boolean} [maintainKeys] - set to true if you don't want keys removed from DOM 
		 * @param {boolean} [restoreDef] - set to true if you don't want the definition to be modified (placeholder, label separator);
		 * best to use only when inline editing is not supported on the result of the template, otherwise labels may not be restored
		 */
		templateToHtml: function(ctx, template, defs, fProcessKey, maintainKeys, restoreDef) {
			var domRoot = (typeof template === "string") ? domConstruct.toDom(ctx.view._stringRepl(template.trim())) : template;
			var repeatKey = this.DATA_TEMPLATE_KEY_REPEAT;
			var attrKey = this.DATA_TEMPLATE_KEY;
			var key;
			var labelSeparator;

			function render(def, domNode) {
				var p0;
				var l0;

				if (def) {
					p0 = def.bPlaceholder;
					l0 = def.labelSeparator;

					if (labelSeparator == "") {
						def.bPlaceholder = true;
					} else if (labelSeparator) {
						def.labelSeparator = labelSeparator;
					} else {
						def.bPlaceholder = def.bPlaceholder || false;
					}
				}

				fProcessKey(ctx, key, def, domNode);

				// restore previous values
				if (def && restoreDef) {
					def.bPlaceholder = p0;
					def.labelSeparator = l0;
				}
			}

			fProcessKey = fProcessKey || _defaultProcessKey;
			ctx.applyNodeContext(domRoot);

			// process all rules before any other keys since rules can remove content from template
			_processRules(ctx, domRoot, defs);

			// process all other keys
			query(html.getDataAttributeSelector(attrKey), domRoot).forEach(function(domNode) {
				key = html.getDataAttribute(domNode, attrKey);
				labelSeparator = html.getDataAttribute(domNode, html.DATA_TEMPLATE_LABEL);

				var def = defs[key];

				if (def || !maintainKeys) {
					html.removeDataAttribute(domNode, attrKey);
				}

				if (key == repeatKey) {
					// process headings without a specific key
					var domParent = domNode.parentNode;

					_.each(def, function(h) {
						var domCopy = lang.clone(domNode);
						domParent.insertBefore(domCopy, domNode);
						render(h, domCopy);
					});

					// remove template for repeating headings
					if (def || !maintainKeys) {
						domParent.removeChild(domNode);
					}
				} else {
					// process fixed key
					render(def, domNode);
				}
			});

			return domRoot;
		}
	});

	function _assignKey(/*Object*/obj, /*XMLNode*/def) {
		obj.key = util.getProperty(def, "Key", obj.m_id);
		obj.key = (obj.key == null) ? null : String(obj.key);
		obj.bFixedKey = obj.key && (obj.key != "*");
	}

	function _processTag(/*XML*/def) {
		var ctr = TAGS[def.tagName];
		var obj;

		if (ctr) {
			obj = new ctr(def);
		} else {
			// because action tags can be different, need to use different method
			var action = ActionFactory.newAction(def);
			if (action) {
				obj = new Action(action);
			}
		}

		if (obj) {
			_assignKey(obj, def);
		}

		return obj;
	}

	return declare(View, {
		defaultImageSize: reflection.IMAGE_SIZE_USE_LARGE_IMAGE,
		_types: ["Html"],

		// {String:html.*}: definitions mapped by key
		defsByKey: null,

		// Heading[]: array of all headings in view (m_headings only contains those in custom view - not fixed)
		allHeadings: null,

		constructor: function(def) {
			const defsByKey = this.defsByKey = {};
			const defs = this.m_defs = util.getDefs(def, "child::*", _processTag, true) || [];

			this.bShowLabels = util.getBooleanProperty(def, "ShowLabels", true);
			this.templatePath = util.getProperty(def, "Template");
			this.m_headings = defs.filter((obj) => obj instanceof Heading);

			defs.forEach((obj) => {
				if (obj.bFixedKey) {
					defsByKey[obj.key] = obj;
				}
			});
		},

		addResource: function(key, bFixedKey, obj) {
			// summary: add resource definition to view if not already present
			if (this.defsByKey[key]) {
				return false;
			}

			if (obj instanceof Heading) {
				this.m_headings.push(obj);
			}

			obj.key = String(key);
			obj.bFixedKey = bFixedKey;

			this.m_defs.push(obj);

			if (bFixedKey) {
				this.defsByKey[key] = obj;
			}

			return true;
		},

		removeResource: function(obj) {
			// summary: remove resource definition from view
			delete this.defsByKey[obj.key];
			this.defs = _.without(this.defs, obj);
		},

		setResourceKey: function(obj, key, bFixedKey) {
			// summary: change resource's key
			delete this.defsByKey[obj.key];
			obj.key = String(key);
			obj.bFixedKey = bFixedKey;

			if (bFixedKey) {
				this.defsByKey[obj.key] = obj;
			}
		},

		getActions: function() {
			var a = this.inherited(arguments);
			var b = _.filter(this.m_defs, function(obj) {return obj.isAction;});

			return b.concat(a);
		},

		/**
		 * Return list of fixed headings in view
		 * @return {Heading[]}
		 */
		getFixedHeadings() {
			const a = [];

			_.each(this.m_defs, (obj) => {
				if (obj instanceof Heading && obj.bFixedKey) {
					a.push(obj);
				}
			});

			return a;
		},

		getHeadings: function() {
			// summary: return all headings shown in view - add headings with fixed keys to custom headings
			return this.allHeadings || this.m_headings;
		},

		getViewRefs: function() {
			// summary: return array of view references
			return _.filter(this.m_defs, function(obj) {return obj instanceof ViewRef;});
		},

		newHeading: function(def) {
			var obj = new Heading(def);
			_assignKey(obj, def);
			return obj;
		},

		onRemoveHeading: function(/*Heading*/h) {
			// summary: remove heading from defs array and recalculate hashtable to keep in sync with headings
			if (this.m_defs.dele(h)) {
				if (h.bFixedKey) {
					delete this.defsByKey[h.key];
				}
			}
		},

		onAddHeading: function(/*Heading*/h) {
			// summary: add heading to defs and defsByKey
			this.m_defs.push(h);
			if (h.bFixedKey)
				this.defsByKey[h.key] = h;
		},

		resolve: function(/*Class*/rcRoot, /*Node*/nodeRoot, /*Boolean*/bConfigure) {
			// summary: resolve view resources - base class will take care of headings

			// explode cards into headings
			this.populateHeadingsFromCards(rcRoot, this.pathHintsContext);

			var lmap = this.inherited(arguments);

			// resolve other resources in view - if relationship exists, then use its target class to resolve resources
			var rel = this.getPageRelationship();
			if (rel) {
				rcRoot = rel.getTargetClass();
				nodeRoot = null;
			}

			csiDef.resolveDefs(this.m_defs, lmap, rcRoot, nodeRoot);

			return lmap;
		}
	});
});